import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../AuthContext';

/* --------------------------------------------------------------------------
   Discord connect.

   The popup goes to api.schedpilot.com, which sends the user to Discord to add
   the SchedPilot bot to one of their servers, then shows a channel picker. Each
   channel they tick becomes its own account. The popup reports back and
   closes; the accounts list is refreshed either way, because the message can
   be lost if Discord's pages sever window.opener.
   -------------------------------------------------------------------------- */

const API_ORIGIN = 'https://api.schedpilot.com';

// `solid` matches the other platforms' connect cards; the compact outline
// version is for the Reconnect action in the accounts list.
function DiscordConnect({ label = 'Connect Discord', className = 'btn btn-sm w-100', solid = true }) {
  const { getAccountsData } = useAuth();
  const [connecting, setConnecting] = useState(false);

  const popupRef = useRef(null);
  const pollRef = useRef(null);
  const listenerRef = useRef(null);

  const stop = () => {
    clearInterval(pollRef.current);
    if (listenerRef.current) window.removeEventListener('message', listenerRef.current);
    listenerRef.current = null;
    setConnecting(false);
  };

  useEffect(() => stop, []);

  const start = () => {
    // Opened synchronously inside the click handler so popup blockers allow it.
    const width = 520, height = 760;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    popupRef.current = window.open(
      `${API_ORIGIN}/wp-json/api/v1/discord-login`,
      'DiscordAuth',
      `width=${width},height=${height},top=${top},left=${left}`
    );
    if (!popupRef.current) {
      toast.error('Your browser blocked the popup. Allow popups for this site and try again.');
      return;
    }
    setConnecting(true);

    const onMessage = (event) => {
      if (event.origin !== API_ORIGIN) return;   // anything else on the page can postMessage
      const { type, message } = event.data || {};
      if (type === 'discord-authenticated') {
        stop();
        toast.success(message || 'Discord connected.');
        getAccountsData();
      } else if (type === 'discord-error') {
        stop();
        toast.error(message || 'Could not connect Discord.');
      }
    };
    listenerRef.current = onMessage;
    window.addEventListener('message', onMessage);

    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        stop();
        getAccountsData();   // it may have succeeded without the message arriving
      }
    }, 700);
  };

  return (
    <button
      type="button"
      className={className}
      onClick={start}
      disabled={connecting}
      style={solid
        ? { background: '#5865F2', border: 'none', borderRadius: 7, color: '#fff', fontWeight: 600, padding: '8px 0' }
        : { border: '1px solid #c7cdf8', borderRadius: 7, color: '#4752c4', fontWeight: 600, background: '#fff' }}
    >
      {connecting ? 'Waiting for Discord…' : label}
    </button>
  );
}

export { DiscordConnect };
